/**
 * iter87.212.31 — Detailed live score cards for soccer tournaments,
 * matching NflLiveTicker's visual treatment so every sport gets the
 * same "detailed scores at top" experience (was football-only before
 * this, with soccer stuck on the scrolling LiveGameStrip alone).
 *
 * iter87.359 (user-requested — "I like the logic the NFL ticker is
 * using... could that be applied to MLB and club soccer (all the
 * games)?") — now renders live + upcoming-soon + recently-finished
 * games (NFL's exact ±4.5h/8h kickoff window, via `useScheduleLive`'s
 * `tickerEntries`), not just strictly-live games. Also used by MLB/NBA
 * Playoffs (see LeagueDashboard) since they share the same entry shape.
 *
 * Zero extra vendor/API cost — reuses the schedule already fetched by
 * `useScheduleLive` for the (now-hidden-when-this-renders) scrolling
 * strip, just re-shaped into these cards.
 */
import { ArrowRight } from "lucide-react";
import { EPL_TEAM_COLORS } from "@/lib/eplTeamColors";
import { LA_LIGA_TEAM_COLORS } from "@/lib/laLigaTeamColors";
import { MLB_TEAM_COLORS } from "@/lib/mlbTeamColors";
import { readableTextColor } from "@/components/dashboard/TeamSwatch";

function initials(name) {
    const words = (name || "").trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return words.map((w) => w[0]).join("").slice(0, 3).toUpperCase();
}

function hashColor(name) {
    let hash = 0;
    for (let i = 0; i < (name || "").length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) % 360;
    }
    return `hsl(${hash}, 55%, 38%)`;
}

// iter87.258/259 — Solid single-color swatch using real club brand
// colors (EPL then La Liga lookup), mirroring NflLiveTicker's
// treatment. Clubs from any other league sharing this ticker fall
// back to a deterministic solid hash color — same as before.
// iter87.260 — reverted from two-tone diagonal-split to a single
// primary color per operator directive.
// iter87.314 (user-reported, "weird colors" on MLB cards) — added
// MLB_TEAM_COLORS to the lookup chain (same real brand-color map the
// Team Pick Pot picks section already uses via `mlbTeamColorPair`) so
// MLB teams stop falling through to the hash fallback. NBA/soccer
// unaffected — user wants those on a separate P1 pass, not now.
function teamColorPair(name) {
    return EPL_TEAM_COLORS[name] || LA_LIGA_TEAM_COLORS[name] || MLB_TEAM_COLORS[name] || [hashColor(name), hashColor(name)];
}

const kickoffLabel = (iso) => {
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
};

// iter87.359 — mirrors NflLiveTicker's `statusBadge()`: live games get
// their in-progress detail (or "LIVE"), finished games get "FINAL",
// everything else gets its kickoff time.
const statusBadge = (e) => {
    if (e.status === "live") return { label: e.live_detail || "LIVE", live: true };
    if (e.played) return { label: "FINAL", live: false };
    return { label: kickoffLabel(e.scheduled_at), live: false };
};

function TeamRow({ name, score, mine }) {
    const [primary] = teamColorPair(name);
    // iter87.511 (user-reported — same white-on-light-color fix as
    // TeamSwatch.jsx) — the halo direction flips with the text color
    // so it still reads as an outline, not a smudge, either way.
    const textColor = readableTextColor(primary);
    const haloColor = textColor === "#000000" ? "#fff" : "#000";
    return (
        <div className="flex items-center gap-1.5 min-w-0">
            <span
                className="w-4 h-4 shrink-0 rounded-full flex items-center justify-center text-[7px] font-black tracking-tighter leading-none"
                style={{
                    background: primary,
                    color: textColor,
                    textShadow: `-1px -1px 0 ${haloColor}, 1px -1px 0 ${haloColor}, -1px 1px 0 ${haloColor}, 1px 1px 0 ${haloColor}`,
                }}
                data-testid="epl-team-swatch"
            >
                {initials(name)}
            </span>
            <span className={`text-[11px] truncate ${mine ? "text-volt font-bold" : "text-zinc-300"}`}>{name}</span>
            <span className="ml-auto text-[11px] font-mono text-zinc-400">
                {score ?? "–"}
            </span>
        </div>
    );
}

export default function SoccerLiveTicker({ entries = [], myTeamIds = [], onOpenSchedule }) {
    if (!entries.length) return null;
    const liveWindow = entries.some((e) => e.status === "live");

    return (
        <div className="border border-[#222] bg-[#0a0a0a] px-3 py-2 rounded-lg" data-testid="soccer-live-ticker">
            <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                    {liveWindow ? "Games On Now" : "Today's Games"}
                </span>
                {liveWindow && (
                    <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-neon" data-testid="soccer-live-ticker-live-pill">
                        <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse" /> Live
                    </span>
                )}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="soccer-live-ticker-games">
                {entries.map((e) => {
                    const b = statusBadge(e);
                    const homeMine = myTeamIds.includes(e.home_team_id);
                    const awayMine = myTeamIds.includes(e.away_team_id);
                    const mine = homeMine || awayMine;
                    const border = b.live ? "border-neon/50 bg-neon/5" : "border-[#222] bg-[#111]";
                    return (
                        <div
                            key={e.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => onOpenSchedule?.(e)}
                            onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") onOpenSchedule?.(e); }}
                            className={`shrink-0 w-44 p-2 rounded-lg border ${border} ${mine ? "border-l-2 border-l-volt bg-volt/5" : ""} ${onOpenSchedule ? "cursor-pointer hover:brightness-125 transition-[filter]" : ""}`}
                            data-testid={`soccer-live-ticker-game-${e.id}`}
                            data-my-matchup={mine ? "true" : undefined}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-[9px] uppercase tracking-wider text-zinc-500">
                                    {mine ? <span className="text-volt font-bold">MY MATCHUP</span> : (e.round_label || "")}
                                </span>
                                <span className={`text-[9px] font-bold uppercase tracking-wider ${b.live ? "text-neon" : e.played ? "text-zinc-400" : "text-cyber"}`}>
                                    {b.label}
                                </span>
                            </div>
                            <div className="space-y-0.5">
                                <TeamRow name={e.away_label} score={e.score_b} mine={awayMine} />
                                <TeamRow name={e.home_label} score={e.score_a} mine={homeMine} />
                            </div>
                        </div>
                    );
                })}
                {onOpenSchedule && (
                    <button
                        type="button"
                        onClick={() => onOpenSchedule()}
                        data-testid="soccer-live-ticker-view-schedule"
                        className="shrink-0 flex items-center gap-1 px-2 text-[10px] font-bold uppercase text-zinc-500 hover:text-neon transition-colors"
                    >
                        Schedule <ArrowRight className="w-3 h-3" />
                    </button>
                )}
            </div>
        </div>
    );
}
